import Ajv, { type ErrorObject } from 'ajv'; // https://github.com/epoberezkin/ajv
import addTypeofKeyword from 'ajv-keywords/keywords/typeof.js';
import { type JsonSchemaObject } from '@knapsack/types';
import { z } from 'zod';

export const ajv = new Ajv();

addTypeofKeyword(ajv);

type ajvErrors = ErrorObject[];

const ajvDefaults = new Ajv({
  useDefaults: true,
});

/**
 * Validate Schema itself (metaSchema)
 * Validates against contents of the `$schema` field
 * @link https://github.com/epoberezkin/ajv#validateschemaobject-schema---boolean
 */
export function validateSchema(schema: Record<string, unknown>): {
  ok: boolean;
  message: string;
} {
  const isSchemaValid = ajv.validateSchema(schema);
  return {
    ok: isSchemaValid,
    message: isSchemaValid
      ? ''
      : `Schema itself is not valid. ${ajv.errorsText()}`,
  };
}

/**
 * Validates a {@link JsonSchemaObject} using Zod
 */
export const jsonSchemaZodSchema = z
  .record(z.string(), z.unknown())
  .superRefine((data, ctx) => {
    const { ok, message } = validateSchema(data);
    if (!ok) {
      ctx.addIssue({
        code: 'custom',
        message,
      });
    }
  })
  .transform((data) => data as JsonSchemaObject);

/**
 * Validate Schema and Assign Defaults
 * Any `default` on properties will be added if that property is absent
 * @link https://github.com/epoberezkin/ajv#assigning-defaults
 */
export function validateSchemaAndAssignDefaults<T>(
  schema: Record<string, unknown>,
  data: T,
): { ok: boolean; message: string; data: T } {
  const { ok, message } = validateSchema(schema);
  if (!ok) {
    return {
      ok: false,
      message,
      data,
    };
  }

  const newData = { ...data };
  // This `validate` function mutates `newData`, so that's why we created a new object above first.
  const isValid = !!ajvDefaults.validate(schema, newData);
  return {
    ok: isValid,
    message: isValid ? '' : ajvDefaults.errorsText(),
    data: newData,
  };
}

/**
 * Validate Data against Schema
 * @link https://github.com/epoberezkin/ajv
 */
export function validateDataAgainstSchema<T>(
  schema: Record<string, unknown>,
  data: T,
): {
  ok: boolean;
  message: string;
  errors?: ajvErrors;
} {
  const { ok, message } = validateSchema(schema);
  if (!ok) {
    return {
      ok: false,
      message,
    };
  }

  const isValid = !!ajv.validate(schema, data);
  return {
    ok: isValid,
    message: isValid ? '' : ajv.errorsText(),
    errors: isValid ? [] : ajv.errors || [],
  };
}

//
